/* V52 — mobile search behaviour: query-state discovery hiding, recent searches, count localisation. */
(() => {
  const STORAGE_KEY = 'vc_recent_searches_v1';
  const MAX_RECENT = 5;

  const safeRead = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value)
        ? value.filter((item) => typeof item === 'string' && item.trim()).slice(0, MAX_RECENT)
        : [];
    } catch (_) {
      return [];
    }
  };

  const safeWrite = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_RECENT)));
    } catch (_) {}
  };

  const renderRecent = () => {
    const section = document.querySelector('[data-vc-search-discovery]');
    const wrapper = document.querySelector('[data-vc-recent-searches]');
    const list = document.querySelector('[data-vc-recent-list]');
    if (!section || !wrapper || !list) return;

    const items = safeRead();
    list.replaceChildren();
    wrapper.hidden = items.length === 0;
    if (!items.length) return;

    const searchUrl = section.dataset.searchUrl || '/search';
    items.forEach((term) => {
      const link = document.createElement('a');
      link.textContent = term;
      link.href = `${searchUrl}?q=${encodeURIComponent(term)}&type=product`;
      list.appendChild(link);
    });
  };

  const rememberQuery = (query) => {
    const cleaned = (query || '').trim();
    if (!cleaned) return;
    const items = safeRead().filter((item) => item.toLowerCase() !== cleaned.toLowerCase());
    items.unshift(cleaned);
    safeWrite(items);
  };

  const syncQueryState = () => {
    const input = document.querySelector('#Search-In-Template');
    const hasQuery = Boolean(input && input.value.trim());
    document.body.classList.toggle('vc-search-querying', hasQuery && !document.body.classList.contains('vc-search-results-page'));
  };

  const bindForm = () => {
    const input = document.querySelector('#Search-In-Template');
    const form = input?.closest('form[role="search"]');
    if (!input || !form) return;

    if (new URLSearchParams(window.location.search).get('q')) {
      document.body.classList.add('vc-search-results-page');
    }

    input.addEventListener('input', syncQueryState);
    input.addEventListener('search', syncQueryState);
    form.addEventListener('reset', () => requestAnimationFrame(syncQueryState));
    form.addEventListener('submit', () => rememberQuery(input.value));
    syncQueryState();
  };

  const bindClear = () => {
    document.querySelector('[data-vc-recent-clear]')?.addEventListener('click', () => {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (_) {}
      renderRecent();
    });
  };

  const syncPredictiveState = () => {
    const predictive = document.querySelector('predictive-search');
    if (!predictive) return;

    const sync = () => {
      document.body.classList.toggle('vc-search-live-open', predictive.hasAttribute('open'));
    };

    sync();
    new MutationObserver(sync).observe(predictive, {
      attributes: true,
      attributeFilter: ['open', 'loading'],
    });
  };

  const localizeResultCounts = () => {
    document.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count').forEach((node) => {
      let count = node.dataset?.productCount;
      if (!count) {
        const match = (node.textContent || '').match(/\d[\d,]*/);
        count = match ? match[0].replace(/,/g, '') : '';
      }
      if (!count) return;
      const next = `${count} 个结果`;
      if ((node.textContent || '').trim() !== next) node.textContent = next;
    });
  };

  const watchResultCounts = () => {
    localizeResultCounts();
    const root = document.querySelector('#MainContent');
    if (!root) return;
    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        localizeResultCounts();
      });
    }).observe(root, { childList: true, subtree: true });
  };

  const init = () => {
    renderRecent();
    bindForm();
    bindClear();
    syncPredictiveState();
    watchResultCounts();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
/* V53 — search-page hardening: ensure predictive search is live and tidy mobile toolbar labels. */
(() => {
  const init = () => {
    if (!document.body.classList.contains('template-search')) return;

    /* The search page should always behave as a live-search surface. */
    const input = document.querySelector('#Search-In-Template');
    const predictive = input?.closest('predictive-search');
    if (input && predictive) {
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('autocorrect', 'off');
      input.setAttribute('autocapitalize', 'off');
      input.setAttribute('spellcheck', 'false');

      /* If the theme editor restores a typed value, kick the native component once. */
      if (input.value.trim() && !predictive.hasAttribute('results')) {
        requestAnimationFrame(() => input.dispatchEvent(new Event('input', { bubbles: true })));
      }
    }

    /* Shorter label reads cleaner beside the result count on a 390px screen. */
    document.querySelectorAll('.mobile-facets__open-label.medium-hide.large-up-hide').forEach((label) => {
      if (label.textContent.trim() === '筛选与排序') label.textContent = '筛选 / 排序';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
/* V54 — dedicated mobile live-search panel for the search page.
   This intentionally does not rely on Dawn's dropdown being nested inside the rounded input field. */
(() => {
  const isSearchPage = () => document.body.classList.contains('template-search');
  const MIN_LATIN = 2;
  const LIMIT = 6;
  let timer = null;
  let controller = null;
  let lastQuery = '';

  const hasEnoughCharacters = (value) => {
    const q = value.trim();
    if (!q) return false;
    return /[^\x00-\x7F]/.test(q) ? q.length >= 1 : q.length >= MIN_LATIN;
  };

  const normalizeImage = (product) => {
    const image = product?.image || product?.featured_image || product?.featured_media?.preview_image;
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  };

  const formatPrice = (product) => {
    const raw = product?.price_min ?? product?.price;
    if (raw === null || raw === undefined || raw === '') return '';
    const str = String(raw).trim();
    if (/[^\d.,-]/.test(str)) return str;
    const amount = Number(str.replace(/,/g, ''));
    if (!Number.isFinite(amount)) return str;
    const locale = document.documentElement.lang || 'en-GB';
    const currency = document.documentElement.dataset.currency || window.Shopify?.currency?.active || 'GBP';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch (_) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const buildSearchAllUrl = (form, query) => {
    const base = form?.getAttribute('action') || '/search';
    return `${base}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last&type=product`;
  };

  const setOpen = (panel, open) => {
    panel.hidden = !open;
    document.body.classList.toggle('vc-custom-live-open', open);
  };

  const renderLoading = (panel, query) => {
    panel.replaceChildren();
    const row = create('div', 'vc-live-search-panel__loading');
    const dot = create('i', 'vc-live-search-panel__spinner');
    const text = create('span', '', `正在查找“${query}”…`);
    row.append(dot, text);
    panel.append(row);
    setOpen(panel, true);
  };

  const renderError = (panel, form, query) => {
    panel.replaceChildren();
    const link = create('a', 'vc-live-search-panel__fallback', `查看“${query}”的搜索结果 →`);
    link.href = buildSearchAllUrl(form, query);
    panel.append(link);
    setOpen(panel, true);
  };

  const renderResults = (panel, form, query, products) => {
    panel.replaceChildren();

    const head = create('div', 'vc-live-search-panel__head');
    head.append(create('strong', '', '匹配商品'), create('small', '', `${products.length} 个`));
    panel.append(head);

    const list = create('div', 'vc-live-search-panel__list');
    products.forEach((product) => {
      const link = create('a', 'vc-live-search-panel__item');
      link.href = product.url || buildSearchAllUrl(form, query);

      const media = create('div', 'vc-live-search-panel__media');
      const imageUrl = normalizeImage(product);
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = product.title || '';
        img.width = 48;
        img.height = 48;
        img.loading = 'eager';
        img.decoding = 'async';
        media.append(img);
      } else {
        media.append(create('span', '', 'VC'));
      }

      const copy = create('div', 'vc-live-search-panel__copy');
      copy.append(create('strong', '', product.title || '商品'));
      if (product.vendor) copy.append(create('small', '', product.vendor));
      const price = formatPrice(product);
      if (price) copy.append(create('b', '', price));

      const arrow = create('i', 'vc-live-search-panel__arrow', '→');
      link.append(media, copy, arrow);
      list.append(link);
    });
    panel.append(list);

    const all = create('a', 'vc-live-search-panel__all', products.length ? `查看全部“${query}”结果` : `搜索“${query}”`);
    all.href = buildSearchAllUrl(form, query);
    all.append(create('span', '', '→'));
    panel.append(all);
    setOpen(panel, true);
  };

  const fetchResults = async (input, panel, form) => {
    const query = input.value.trim();
    if (!hasEnoughCharacters(query)) {
      lastQuery = '';
      if (controller) controller.abort();
      setOpen(panel, false);
      panel.replaceChildren();
      return;
    }

    lastQuery = query;
    if (controller) controller.abort();
    controller = new AbortController();
    renderLoading(panel, query);

    const base = window.routes?.predictive_search_url || '/search/suggest';
    const params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', String(LIMIT));
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,vendor,product_type,variants.title,tag');

    try {
      const response = await fetch(`${base}.json?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Search ${response.status}`);
      const data = await response.json();
      if (input.value.trim() !== query) return;
      const products = data?.resources?.results?.products || [];
      renderResults(panel, form, query, products);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (input.value.trim() !== query) return;
      renderError(panel, form, query);
    }
  };

  const init = () => {
    if (!isSearchPage()) return;
    const input = document.querySelector('[data-vc-live-search-input]');
    const panel = document.querySelector('[data-vc-live-search-panel]');
    const form = input?.closest('form[role="search"]');
    if (!input || !panel || !form || input.dataset.vcLiveBound === 'true') return;
    input.dataset.vcLiveBound = 'true';

    const queue = () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (!hasEnoughCharacters(query)) {
        if (controller) controller.abort();
        panel.replaceChildren();
        setOpen(panel, false);
        return;
      }
      timer = setTimeout(() => fetchResults(input, panel, form), 160);
    };

    input.addEventListener('input', queue);
    input.addEventListener('focus', () => {
      if (hasEnoughCharacters(input.value)) queue();
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(panel, false);
    });
    form.addEventListener('reset', () => {
      requestAnimationFrame(() => {
        panel.replaceChildren();
        setOpen(panel, false);
      });
    });

    document.addEventListener('pointerdown', (event) => {
      if (!panel.hidden && !panel.contains(event.target) && !form.contains(event.target)) {
        setOpen(panel, false);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
